/**
 * SystemLandingPage - Visar alla moduler för ett system som klickbara kort
 */

import { useState, useEffect, useMemo } from 'react';
import { ROUTES } from '@/constants/routes';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage, useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import { withBackTo } from '@/utils/navigationBack';
import { PageBackButton } from '@/components/common/PageBackButton';
import api from '@/services/api/client';
import { oldBookingApi } from '@/services/booking/oldBookingApi';
import { isGlobalAdminUser } from '@/utils/isGlobalAdminUser';
import { getIconColors, getModuleIcon } from '@/utils/moduleIcons';
import { tModule } from '@/utils/moduleLabels';
import { groupModules, moduleCountLabel, sidebarGroupId } from '@/utils/navGroups';
import { isDesignPreviewHost } from '@/utils/designPreview';
import { bookingCreateActions, moduleMatchesQuery } from '@/utils/systemLanding';
import SystemModuleCard from '@/pages/system/SystemModuleCard';
import { resolveWorkshopModuleRoute } from '@/utils/workshopModuleRoutes';
import * as HeroIcons from '@heroicons/react/24/outline';

interface Module {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  icon?: string;
  route?: string;
  sortOrder: number;
  isDefault: boolean;
}

interface System {
  id: string;
  name: string;
  displayName: string;
  description?: string;
  icon?: string;
  color?: string;
  route?: string;
  modules: Module[];
}

function ComingSoonContent({ systemName }: { systemName: string }) {
  const { t } = useTranslation();
  const comingSoonKey = `comingSoon${systemName.charAt(0).toUpperCase() + systemName.slice(1)}`;
  const featuresObj = t(`systemPage.${comingSoonKey}`) as Record<string, string> | string | undefined;
  const features = featuresObj && typeof featuresObj === 'object'
    ? Object.values(featuresObj)
    : [];

  return (
    <div className="max-w-2xl mx-auto text-center py-8">
      <div className="flex justify-center mb-8">
        <div className="inline-flex items-center gap-2 px-6 py-3 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 rounded-full">
          <HeroIcons.ClockIcon className="w-5 h-5" />
          <span className="font-semibold">{t('systemPage.comingSoon')}</span>
        </div>
      </div>

      {features.length > 0 && (
        <div className="bg-bgElevated rounded-xl border border-border p-8 text-left">
          <h2 className="text-xl font-semibold text-textDefault mb-6 flex items-center gap-2">
            <HeroIcons.SparklesIcon className="w-6 h-6 text-bgPrimary" />
            {t('systemPage.plannedFeatures')}
          </h2>
          <ul className="space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="p-1 bg-green-100 dark:bg-green-900/30 rounded-full mt-0.5">
                  <HeroIcons.CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-textDefault">{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-textTertiary text-sm mt-6">
        {t('systemPage.underDevelopment')}
      </p>
    </div>
  );
}

export default function SystemLandingPage() {
  const { systemName } = useParams<{ systemName: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language } = useLanguage();
  // Get user to refresh when impersonating
  const { user } = useAuth();
  
  const [system, setSystem] = useState<System | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [oldBookingAccess, setOldBookingAccess] = useState(false);
  const [functionQuery, setFunctionQuery] = useState('');
  const designPreview = typeof window !== 'undefined' && isDesignPreviewHost(window.location.hostname);

  const normalize = (value?: string | null): string =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/^\/+/, '');

  useEffect(() => {
    // Clear old data when user changes (impersonation)
    setSystem(null);
    
    const fetchSystem = async () => {
      try {
        setLoading(true);
        setError(null);
        // Use /api/menu to get filtered systems/modules based on customer packages and user role
        const response = await api.get<{ success: boolean; data: { systems: System[] }; error?: string }>('/api/menu');
        
        if (response.data.success && response.data.data?.systems) {
          const allSystems = response.data.data.systems;
          const target = normalize(systemName);
          const foundSystem = allSystems.find((s) => {
            const byName = normalize(s.name) === target;
            const byRoute = normalize(s.route) === target;
            const byDisplayName = normalize(s.displayName) === target;
            const byId = normalize(String(s.id)) === target;
            return byName || byRoute || byDisplayName || byId;
          });
          
          if (foundSystem) {
            setSystem(foundSystem);
          } else if (allSystems.length > 0) {
            // Säker fallback: visa första tillgängliga system om exakt match saknas.
            setSystem(allSystems[0]);
          } else {
            setError(t('systemPage.notFoundOrNoAccess').replace('{name}', systemName || ''));
          }
        } else {
          const errorMsg = response.data.error || t('systemPage.apiError');
          setError(errorMsg);
        }
      } catch (error: any) {
        // Hantera olika typer av fel
        if (error?.response?.status === 401) {
          setError(t('systemPage.notLoggedIn'));
          // Omdirigera till login efter 2 sekunder
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        } else if (error?.response?.status === 403) {
          setError(t('systemPage.noPermission'));
        } else if (error?.response?.status >= 500) {
          setError(t('systemPage.serverError'));
        } else {
          const errorMsg = error?.response?.data?.error || error?.message || t('systemPage.unexpectedError');
          setError(errorMsg);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (systemName) {
      fetchSystem();
    } else {
      setError(t('systemPage.noSystemName'));
      setLoading(false);
    }
  }, [systemName, user?.id]); // Refetch when user changes (impersonation)

  useEffect(() => {
    if (systemName !== 'booking') {
      setOldBookingAccess(false);
      return;
    }
    if (isGlobalAdminUser(user)) {
      setOldBookingAccess(true);
      return;
    }
    oldBookingApi
      .checkAccess()
      .then((r) => setOldBookingAccess(r.hasAccess))
      .catch(() => setOldBookingAccess(false));
  }, [systemName, user?.id, user?.is_global_admin]);

  const displayModules = useMemo(() => {
    const base = (system?.modules || []).filter((module) => module.name !== 'live-tracking');
    const extras: Module[] = [];
    if (system?.name === 'booking' && oldBookingAccess) {
      extras.push({
        id: 'old-booking-temp',
        name: 'old-booking',
        displayName: 'Old Booking',
        description: '',
        icon: 'ClockIcon',
        route: '/booking/old-booking',
        sortOrder: 99,
        isDefault: false,
      });
    }
    if (normalize(system?.name) === 'keymanagement' && isGlobalAdminUser(user)) {
      extras.push({
        id: 'cabinet-sync-temp',
        name: 'cabinet-sync',
        displayName: t('modules.cabinet-sync.title'),
        description: '',
        icon: 'SignalIcon',
        route: '/system/keymanagement/manage?tab=cabinetSync',
        sortOrder: 98,
        isDefault: false,
      });
    }
    return extras.length ? [...base, ...extras] : base;
  }, [system, oldBookingAccess, t, user]);

  const visibleModules = useMemo(() => {
    if (!designPreview) return displayModules;
    return displayModules.filter((module) => {
      const title = tModule(t, module.name, 'title', system?.name) || module.displayName;
      const description = tModule(t, module.name, 'description', system?.name) || module.description || '';
      return moduleMatchesQuery(title, description, functionQuery);
    });
  }, [designPreview, displayModules, functionQuery, system?.name, t]);

  const moduleSections = useMemo(() => {
    return groupModules(
      system?.name || '',
      visibleModules,
      (module) => tModule(t, module.name, 'title', system?.name) || module.displayName,
      language
    );
  }, [visibleModules, system?.name, t, language]);

  const createActions = useMemo(
    () => bookingCreateActions(displayModules.map((module) => module.name)),
    [displayModules]
  );

  const handleModuleClick = (module: Module) => {
    const route = resolveWorkshopModuleRoute(system?.name, module.name, module.route);
    if (route) {
      navigate(route, withBackTo(`/system/${systemName}`));
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-bgPrimary"></div>
        <p className="ml-4 text-textSecondary">{t('systemPage.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 bg-bgContainer min-h-full">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
            <div className="flex items-start gap-4">
              <HeroIcons.ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">
                  {t('systemPage.errorTitle')}
                </h2>
                <p className="text-red-700 dark:text-red-400 text-sm mb-4">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    setLoading(true);
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm transition-colors"
                >
                  {t('systemPage.tryAgain')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!system) {
    return (
      <div className="p-8 bg-bgContainer min-h-full">
        <div className="max-w-2xl mx-auto text-center">
          <HeroIcons.QuestionMarkCircleIcon className="w-16 h-16 text-textTertiary mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-textDefault mb-2">{t('systemPage.systemNotFound')}</h2>
          <p className="text-textSecondary mb-4">
            {t('systemPage.systemNotFoundDescription').replace('{name}', systemName || '')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-bgPrimary hover:bg-bgPrimary/90 text-white rounded-lg transition-colors"
          >
            {t('systemPage.backToStart')}
          </button>
        </div>
      </div>
    );
  }


  const systemTitle = tModule(t, system.name, 'title') || system.displayName;
  const showQuickBooking = system.name === 'booking' && createActions.quick;
  const showRegularBooking = system.name === 'booking' && createActions.regular;

  return (
    <div className="p-6 bg-bgContainer min-h-full">
      {designPreview ? (
        <div className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <p className="text-xs text-textTertiary">
              {t(`sidebar.groups.${sidebarGroupId(system.name)}`)}
              <span className="px-1.5">/</span>
              {systemTitle}
            </p>
            <div className="mt-1 flex items-center gap-3">
              <div className={`rounded-xl p-2 ${getIconColors(system.icon).bgClass} ${getIconColors(system.icon).textClass}`}>
                {getModuleIcon(system.icon, 'w-6 h-6')}
              </div>
              <h1 className="truncate text-2xl font-semibold text-textDefault">{systemTitle}</h1>
            </div>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <label className="relative block">
              <span className="sr-only">{t('systemPage.searchFunctions').replace('{name}', systemTitle)}</span>
              <HeroIcons.MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textTertiary" />
              <input
                value={functionQuery}
                onChange={(event) => setFunctionQuery(event.target.value)}
                placeholder={t('systemPage.searchFunctions').replace('{name}', systemTitle)}
                className="w-full rounded-lg border border-border bg-bgElevated py-2 pl-9 pr-3 text-sm text-textDefault placeholder:text-textTertiary sm:w-64"
              />
            </label>
            {showQuickBooking && (
              <button
                type="button"
                onClick={() => navigate('/booking/quick', withBackTo(`/system/${systemName}`))}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-border bg-bgElevated px-3 py-2 text-sm font-medium text-textDefault hover:border-bgPrimary"
              >
                <HeroIcons.BoltIcon className="h-4 w-4" />
                {tModule(t, 'quick-booking', 'title', 'booking') || t('booking.quickTitle')}
              </button>
            )}
            {showRegularBooking && (
              <button
                type="button"
                onClick={() => navigate('/booking/calendar?newBooking=1', withBackTo(`/system/${systemName}`))}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-bgPrimary px-3 py-2 text-sm font-medium text-white hover:bg-bgPrimary/90"
              >
                <HeroIcons.PlusIcon className="h-4 w-4" />
                {t('booking.newBooking')}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <PageBackButton fallback={ROUTES.HOME} />
            <div className={`p-3 rounded-xl ${getIconColors(system.icon).bgClass} ${getIconColors(system.icon).textClass}`}>
              {getModuleIcon(system.icon, 'w-10 h-10')}
            </div>
            <div>
              <h1 className="text-3xl font-bold text-textDefault">{systemTitle}</h1>
              {(tModule(t, system.name, 'description') || system.description) && (
                <p className="text-textSecondary mt-1">{tModule(t, system.name, 'description') || system.description}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {visibleModules.length > 0 ? (
        <div className="space-y-8">
          {moduleSections.map((section) => (
            <section key={section.id}>
              <div className="mb-3 flex items-baseline gap-3">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-textTertiary">
                  {t(section.labelKey)}
                </h2>
                <span className="text-xs text-textTertiary">{moduleCountLabel(t, section.items.length)}</span>
              </div>
              <div className={designPreview
                ? 'grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5'
                : 'grid grid-cols-1 items-stretch gap-6 md:grid-cols-2'}
              >
                {section.items.map((module) => (
                  <SystemModuleCard
                    key={module.id}
                    name={module.name}
                    icon={module.icon}
                    title={tModule(t, module.name, 'title', system.name) || module.displayName}
                    description={tModule(t, module.name, 'description', system.name) || module.description}
                    systemName={system.name}
                    dense={designPreview}
                    onOpen={() => handleModuleClick(module)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <p className="text-textSecondary">
          {functionQuery.trim() ? t('systemPage.noSearchHits') : t('systemPage.noModules')}
        </p>
      )}

      {displayModules.length === 0 && (
        <ComingSoonContent systemName={systemName || ''} />
      )}
    </div>
  );
}

